import React, { useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useSatisfactionByShift } from '../../hooks/useSatisfactionQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { ChartControls, ShiftTimeHint, HorizontalTableShell } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { getChartColors, getChartBackgroundColor, getChartJsOptions, formatTooltipLabel, mergeChartOptions } from '../../utils/themeHelpers';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';
import { TimePeriod } from '../../types/api';
import { logger } from '../../utils/logger';

interface ShiftSatisfactionProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const ShiftSatisfaction: React.FC<ShiftSatisfactionProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: shiftData, isLoading, error } = useSatisfactionByShift(branchId, timeRange);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  
  // Component metadata
  const componentId = 'shift-satisfaction';
  const componentTitle = 'رضایت مشتریان بر اساس شیفت کاری';

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  // Use theme-aware colors
  const chartColors = getChartColors(currentTheme);

  const shiftColorMapping = {
    'صبح': 0, // Morning
    'ظهر': 1, // Afternoon
    'عصر': 2, // Evening (First Night Shift)
    'شب': 3, // Late Night (Second Night Shift)
    // Also keep time format for backward compatibility
    '02:00 t 12:00': 0, // Morning
    '12:00 t 16:00': 1, // Afternoon
    '16:00 t 20:00': 2, // Evening
    '20:00 t 02:00': 3, // Night
  };

  // Helper function to get emoji based on satisfaction percentage
  const getSatisfactionEmoji = (percentage: number): string => {
    if (percentage >= 90) return '😍';
    if (percentage >= 80) return '😊';
    if (percentage >= 70) return '🙂';
    if (percentage >= 60) return '😐';
    if (percentage >= 50) return '😕';
    if (percentage >= 40) return '☹️';
    return '😢';
  };

  // Prepare data for shift satisfaction chart
  const prepareShiftChartData = () => {
    if (!shiftData || !shiftData.length) return { labels: [], datasets: [] };

    // Get unique months and shifts (ascending for chart - oldest to newest)
    const months = Array.from(new Set(shiftData.map(d => d.month)))
      .sort((a, b) => {
        const aItem = shiftData.find(d => d.month === a);
        const bItem = shiftData.find(d => d.month === b);
        if (aItem && bItem) {
          return aItem.yearMonth - bItem.yearMonth; // Keep ascending for chart
        }
        return 0;
      });
    
    const shifts = Array.from(new Set(shiftData.map(d => d.shift)));
    
    // Create datasets for each shift
    const datasets = shifts.map(shift => {
      const colorIndex = (shiftColorMapping as any)[shift] || 0;
      const color = chartColors[colorIndex % chartColors.length];
      
      // Create a direct rgba background color with 0.2 opacity
      const rgbaColor = color.startsWith('#') ? 
        `rgba(${parseInt(color.slice(1,3),16)}, ${parseInt(color.slice(3,5),16)}, ${parseInt(color.slice(5,7),16)}, 0.2)` :
        getChartBackgroundColor(colorIndex, currentTheme, 0.2);
        
      return {
        label: shift,
        data: months.map(month => {
          const dataPoint = shiftData.find(d => d.month === month && d.shift === shift);
          return dataPoint ? dataPoint.satisfaction_percentage : null;
        }),
        borderColor: color,
        backgroundColor: rgbaColor,
        borderWidth: 2,
        pointBackgroundColor: color,
        pointBorderColor: currentTheme === 'light' ? '#FFFFFF' : '#27272A',
        pointRadius: 6,
        pointHoverRadius: 10, 
        tension: 0.3,
        fill: false
      };
    });
    
    return { labels: months, datasets };
  };

  // Prepare shift surveys table data
  const prepareShiftTableData = () => {
    if (!shiftData || !shiftData.length) {
      logger.debug('No shift data available for table', 'ShiftSatisfaction');
      return { headers: [], rows: [], shiftHeaders: [] };
    }
    logger.debug('Shift data for table', 'ShiftSatisfaction', shiftData);

    // Get unique months and sort by yearMonth (descending - most recent first)
    const months = Array.from(new Set(shiftData.map(d => d.month)))
      .sort((a, b) => {
        const aItem = shiftData.find(d => d.month === a);
        const bItem = shiftData.find(d => d.month === b);
        if (aItem && bItem) {
          return bItem.yearMonth - aItem.yearMonth; // Changed to descending order
        }
        return 0;
      });
    
    // Define fixed shift names with time ranges
    const predefinedShifts = [
      { name: 'صبح', timeRange: '02:00-12:00' },
      { name: 'ظهر', timeRange: '12:00-16:00' },
      { name: 'عصر', timeRange: '16:00-20:00' },
      { name: 'شب', timeRange: '20:00-02:00' },
      
    ];
    
    // Group by month to show each month as a single row with shift columns
    const monthGroups = new Map<string, number>();
    months.forEach(month => {
      const dataPoints = shiftData.filter(d => d.month === month);
      const monthYearMonth = dataPoints[0]?.yearMonth || 0;
      monthGroups.set(month, monthYearMonth);
    });
    
    // Create a single row for each month with all shifts as columns
    const rows = Array.from(monthGroups.entries()).map(([month, yearMonth]) => {
      const rowData: Record<string, any> = { 
        month,
        yearMonth
      };
      
      let rowTotal = 0;
      let satisfiedTotal = 0;

      // Add each shift as a column, using predefined shift list to ensure consistent display
      predefinedShifts.forEach(shift => {
        const dataPoint = shiftData.find(d => d.month === month && d.shift === shift.name);
        const surveys = dataPoint ? dataPoint.total_surveys : 0;
        rowData[shift.name] = {
          count: surveys,
          satisfaction: dataPoint?.satisfaction_percentage ?? null
        };
        rowTotal += surveys;
        satisfiedTotal += dataPoint?.Satisfiedcount ?? 0;
      });

      rowData['total'] = rowTotal;
      rowData['totalSatisfaction'] = rowTotal > 0 ? (satisfiedTotal / rowTotal) * 100 : null;
      return rowData;
    });
    
    // Sort rows by yearMonth (descending - most recent first)
    rows.sort((a, b) => b.yearMonth - a.yearMonth);
    
    return { 
      headers: ['ماه', ...predefinedShifts.map(s => s.name), 'مجموع'], 
      rows,
      shiftHeaders: predefinedShifts
    };
  };

  // Calculate dynamic Y-axis range
  const calculateDynamicYRange = (data: any[]) => {
    if (!data.length) return { min: 0, max: 105 };
    
    const values = data.flatMap(dataset => 
      dataset.data.filter((value: any) => value !== null && value !== undefined)
    );
    
    if (values.length === 0) return { min: 0, max: 105 };
    
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    
    if (range < 20 && minValue > 20) {
      const padding = Math.max(5, range * 0.2);
      return {
        min: Math.max(0, Math.floor(minValue - padding)),
        max: Math.min(100, Math.ceil(maxValue + padding))
      };
    }
    
    return { min: 0, max: 105 };
  };

  const shiftChartData = prepareShiftChartData();
  const yAxisRange = calculateDynamicYRange(shiftChartData.datasets);

  const chartOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !compact
      },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins.tooltip,
        callbacks: {
          label: (context: any) => {
            if (context.parsed.y === null) return '';
            
            const label = context.dataset.label || '';
            const percentage = context.parsed.y;
            const value = percentage.toFixed(1) + '%';
            const emoji = getSatisfactionEmoji(percentage);
            
            return formatTooltipLabel(label, value);
          }
        }
      }
    },
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      y: {
        ...getChartJsOptions(currentTheme).scales?.y,
        min: yAxisRange.min,
        max: yAxisRange.max,
        title: {
          display: !compact,
          text: 'درصد رضایتمندی',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.y?.ticks,
          callback: (value: any) => { return value + '%'; },
          stepSize: yAxisRange && (yAxisRange.max - yAxisRange.min) < 30 ? 5 : 10,
        }
      },
      x: {
        ...getChartJsOptions(currentTheme).scales?.x,
        title: {
          display: !compact,
          text: 'ماه',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
      }
    },
    elements: (getChartJsOptions(currentTheme) as any).plugins?.elements || {
      point: {
        radius: 0,
        hoverRadius: 8,
        hoverBorderWidth: 3,
        borderWidth: 2,
      },
      line: {
        tension: 0.4,
        borderCapStyle: 'round' as const,
        borderJoinStyle: 'round' as const,
      },
    }
  });

  // Handle loading state
  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="animate-pulse">
          <div className="h-6 bg-bg-secondary rounded w-1/3 mb-4"></div>
          <div className="h-80 bg-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    logger.error('Error loading shift satisfaction data', 'ShiftSatisfaction', error);
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="text-center text-status-error">
          <p>بارگذاری داده‌ها ناموفق بود؛ لطفاً دوباره تلاش کنید.</p>
          <p className="text-sm mt-2">لطفاً صفحه را رفرش کنید یا دوباره تلاش کنید</p>
        </div>
      </div>
    );
  }

  if (!shiftData) {
    logger.debug('No shift data received', 'ShiftSatisfaction');
    return null;
  }

  return (
    <>
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        <div className="mb-6">
          {!compact && (
            <ChartControls
              componentId={componentId}
              componentTitle={componentTitle}
              componentType="mixed"
              dashboardSource="satisfaction"
              onInfo={() => openGuide(CHART_GUIDES.shiftSatisfaction)}
              onFavorite={() => handleFavoriteToggle({
                componentId,
                componentTitle,
                dashboardSource: 'satisfaction',
                componentType: 'mixed',
                description: 'نمودار و جدول رضایت مشتریان بر اساس شیفت کاری',
                props: { branchId, timeRange, onError, showLoading, compact },
                gridLayout: {
                  containerClass: 'card-base card-padding'
                }
              })}
              isFavorited={isFavorited(componentId)}
              className="mb-4"
              chartRef={chartRef}
            />
          )}
          <h3 className="title-card flex items-center gap-2">
            {viewMode === 'chart' ? componentTitle : 'تعداد نظرسنجی‌ها به تفکیک شیفت کاری'}
            <ShiftTimeHint />
          </h3>
          {!compact && (
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    viewMode === 'chart'
                      ? 'bg-primary-600 text-text-inverse shadow-md'
                      : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                  }`}
                  onClick={() => setViewMode('chart')}
                >
                  نمودار رضایت مشتریان
                </button>
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    viewMode === 'table'
                      ? 'bg-primary-600 text-text-inverse shadow-md'
                      : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                  }`}
                  onClick={() => setViewMode('table')}
                >
                  جدول تعداد نظرسنجی‌ها
                </button>
              </div>
            </div>
          )}
        </div>

        {viewMode === 'chart' ? (
          <div className={compact ? "h-60" : "h-80"}>
            <Line 
              data={shiftChartData} 
              options={chartOptions} 
              height={compact ? 240 : 300}
            />
          </div>
        ) : (
          (() => {
            const shiftTable = prepareShiftTableData();
            return (
              <HorizontalTableShell minWidth={Math.max(720, shiftTable.headers.length * 110)}>
                  <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
                    <thead>
                      <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                        {shiftTable.headers.map((header, index) => {
                          const shiftInfo = shiftTable.shiftHeaders.find(s => s.name === header);
                          const isTotal = header === 'مجموع';
                          return (
                            <th
                              key={index}
                              className={`px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 ${isTotal ? '!bg-primary-600 text-text-inverse' : 'text-text-primary'}`}
                              style={{ borderColor: 'var(--border-primary)' }}
                            >
                              <div className="flex flex-col items-center gap-0.5">
                                <span>{header}</span>
                                {shiftInfo && (
                                  <span className={`text-xs font-normal ${isTotal ? 'text-text-inverse/80' : 'text-text-tertiary'}`}>
                                    {shiftInfo.timeRange}
                                  </span>
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {shiftTable.rows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={`transition-all border-b ${rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          {shiftTable.headers.map((header, cellIndex) => {
                            const isTotal = header === 'مجموع';
                            const isMonth = header === 'ماه';

                            if (isMonth) {
                              return (
                                <td
                                  key={cellIndex}
                                  className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-text-primary bg-bg-tertiary"
                                  style={{ borderColor: 'var(--border-primary)' }}
                                >
                                  {row.month}
                                </td>
                              );
                            }

                            const cellData = isTotal
                              ? { count: row.total || 0, satisfaction: row['totalSatisfaction'] as number | null }
                              : (row[header] as { count: number; satisfaction: number | null }) || { count: 0, satisfaction: null };

                            return (
                              <td
                                key={cellIndex}
                                className={`px-3 py-4 whitespace-nowrap text-center text-sm border-l last:border-l-0 ${isTotal ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-bold' : 'text-text-secondary font-medium'}`}
                                style={{ borderColor: 'var(--border-primary)' }}
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <span className="font-semibold">{cellData.count}</span>
                                  {cellData.satisfaction != null && cellData.count > 0 && (
                                    <span className={`text-xs font-normal ${isTotal ? 'text-primary-600 dark:text-primary-300' : 'text-text-tertiary'}`}>
                                      ({cellData.satisfaction.toFixed(1)}%)
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </HorizontalTableShell>
            );
          })()
        )}
      </div>
    </>
  );
};

export default ShiftSatisfaction;